import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner, Text, XStack, YStack } from "tamagui";
import { RiCloseCircleFill } from "react-icons/ri";
import { colors } from "@styles/colors";
import { resolveIPassEntry } from "@utils/ipassMoney";
import { useIPassEntry } from "@utils/store/ipassEntry";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useAuthReady } from "@provider/LineLoginProvider";
import { getRuntimeLiffId } from "@utils/line/getLiffId";
import {
  buildIPassEntryLiffUrl,
  buildIPassEntryLineSchemeUrl,
  isIPassEntryRelayMode,
  markIPassAutoOpened,
  shouldAutoOpenIPassLiff,
} from "@utils/ipassEntryRelay";

/**
 * 入口 A：iPASS MONEY APP 生活繳費 → 我們的固定網址（第三方綁定 V1.5 附錄三）。
 *
 * 網址長這樣：/ipass/entry?iPASSdata=<加密的 otk>&IV=<hex>（一卡通也可能加 supplier / shopCode）
 *
 * 兩種情境：
 *   - 不在 LINE 內（iPASS APP webview／手機瀏覽器）：**中繼**——不登入，把人帶進
 *     liff.line.me/{liffId}/ipass/entry?…（自動試一次 line:// ＋ 手點按鈕），網址參數原樣帶過去。
 *     否則 LIFF 外部登入會在瀏覽器與 LINE 之間無限來回（2026-09-29 pre-prod 實測）。
 *   - 在 LINE 內：
 *     1. 等 LineLoginProvider 把 LIFF 初始化完（要有 LINE access token）
 *     2. 把 iPASSdata / IV 交給後端解析（後端打一卡通 A2/A1 拿姓名／手機）
 *     3. 已註冊 → 鎖定付款方式為 iPASS Money，直接進一鍵下單（/kiosk）
 *        未註冊 → 帶著姓名／手機預填去註冊：網址有 supplier/shopCode 就走該瓦斯行傳單流程，
 *        否則「我是新用戶」；完成後一樣進一鍵下單
 *
 * iPASSdata 是 base64，URLSearchParams 已 decode 一次，**不要**再 decode（+ 會變空白）。
 */
export default function IPassEntry() {
  const relay = isIPassEntryRelayMode();
  return relay ? <RelayToLine /> : <EnterInLine />;
}

function RelayToLine() {
  const liffId = getRuntimeLiffId();
  const search = typeof window !== "undefined" ? window.location.search : "";
  const liffUrl = useMemo(() => buildIPassEntryLiffUrl(liffId, search), [liffId, search]);
  const schemeUrl = useMemo(() => buildIPassEntryLineSchemeUrl(liffId, search), [liffId, search]);
  const [autoTried, setAutoTried] = useState(false);

  useEffect(() => {
    if (schemeUrl && shouldAutoOpenIPassLiff()) {
      markIPassAutoOpened();
      setAutoTried(true);
      window.location.href = schemeUrl;
    }
  }, [schemeUrl]);

  return (
    <YStack f={1} width="100vw" height="100dvh" ai="center" jc="center" bg={colors.bg} px="$6" gap="$5">
      <YStack ai="center" gap="$2">
        <Text fontSize={20} fontWeight="700" color={colors.text} textAlign="center">
          在 LINE 中完成訂購
        </Text>
        <Text fontSize={14} color={colors.sub} textAlign="center" lineHeight={22}>
          {autoTried ? "如果 LINE 沒有自動打開，請點下方按鈕。" : "請點下方按鈕，用 LINE 開啟瓦斯訂購。"}
        </Text>
      </YStack>
      {liffUrl ? (
        <a href={liffUrl} style={{ textDecoration: "none", width: "100%", maxWidth: 360 }}>
          <XStack ai="center" jc="center" bg="#06C755" br={12} py="$4" px="$5" pressStyle={{ opacity: 0.85 }}>
            <Text fontSize={17} fontWeight="700" color="#fff">
              在 LINE 中開啟
            </Text>
          </XStack>
        </a>
      ) : (
        <Text fontSize={13} color={colors.danger} textAlign="center">
          尚未設定 LINE 服務，請稍後再試
        </Text>
      )}
    </YStack>
  );
}

function EnterInLine() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const authReady = useAuthReady();
  const { lineAccessToken } = useLineAuth();
  const { cusJwtToken } = useCusAuth();
  const setEntry = useIPassEntry((s) => s.setEntry);
  const [error, setError] = useState<string | null>(null);
  const fired = useRef(false);

  const iPASSdata = params.get("iPASSdata") || "";
  const iv = params.get("IV") || params.get("iv") || "";
  // 一卡通的固定網址可以自帶 supplier / shopCode（附錄三：他們的參數用 & 接在後面）
  const supplier = (params.get("supplier") || "").trim();
  const shopCode = (params.get("shopCode") || "").trim();

  useEffect(() => {
    if (!authReady || fired.current) return;
    const token = lineAccessToken || sessionStorage.getItem("lineAccessToken") || "";
    if (!token) return; // 還在等 LIFF 登入來回
    if (!iPASSdata || !iv) {
      setError("缺少一卡通轉導參數，請從 iPASS MONEY APP 重新進入");
      return;
    }
    fired.current = true;
    resolveIPassEntry({ iPASSdata, iv, lineAccessToken: token })
      .then((r) => {
        if (r.error) {
          setError(`一卡通資料共享失敗（${r.error.rtn_code}），請回 iPASS MONEY APP 重新授權`);
          return;
        }
        setEntry({ lockPayment: true, prefill: { name: r.profile.name, phone: r.profile.phone } });
        // 已註冊（後端看 LINE 帳號）且前端也拿到 jwt → 直接一鍵下單
        if (r.registered && cusJwtToken) {
          navigate("/kiosk", { replace: true });
          return;
        }
        // 未註冊：有瓦斯行參數就跟掃 QR／傳單一樣進該瓦斯行的 onboarding；沒有就「我是新用戶」（推薦附近瓦斯行）。
        // 兩條走完都會回到一鍵下單，付款方式仍固定 iPASS Money、姓名／手機已預填。
        if (supplier || shopCode) {
          const p = new URLSearchParams();
          if (supplier) p.set("supplier", supplier);
          if (shopCode) p.set("shopCode", shopCode);
          p.set("src", "flyer");
          navigate(`/onboarding/flyer?${p.toString()}`, { replace: true });
        } else {
          navigate("/register/new", { replace: true });
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : "無法連線到服務"));
  }, [authReady, lineAccessToken, cusJwtToken, iPASSdata, iv, supplier, shopCode, navigate, setEntry]);

  return (
    <YStack f={1} ai="center" jc="center" gap={16} p="$4" background={colors.bg}>
      {!error ? (
        <>
          <Spinner size="large" color={colors.primary} />
          <Text color={colors.text}>正在從 iPASS MONEY 取得您的資料…</Text>
        </>
      ) : (
        <>
          <RiCloseCircleFill size={64} color={colors.danger} />
          <Text fontSize={18} fontWeight="700" color={colors.text}>
            無法完成連結
          </Text>
          <Text fontSize={14} color={colors.sub} ta="center">
            {error}
          </Text>
          <Button width="100%" backgroundColor={colors.primary} color="#fff" onPress={() => navigate("/")}>
            回到首頁
          </Button>
        </>
      )}
    </YStack>
  );
}
